import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, ArrowsClockwise, Camera, CheckCircle, X, ShieldCheck, User as UserIcon, Phone, EnvelopeSimple, Buildings, Trophy, Medal, Broadcast, CalendarCheck, XCircle, LockKey } from "@phosphor-icons/react";
import { getAvatarUrl, getInitials } from "@/lib/avatar";
import UserAvatar from "@/components/UserAvatar";

const READER_ID = "dyd-wallet-scanner";

const CONTEXTS = [
  { id: "club_access", label: "Acceso al Club", icon: Buildings,     hint: "Puerta / control físico" },
  { id: "tournament",  label: "Torneo",         icon: Trophy,        hint: "Check-in torneo" },
  { id: "league",      label: "Liga",           icon: Medal,         hint: "Check-in liga" },
  { id: "marcador",    label: "Marcador",       icon: Broadcast,     hint: "Check-in marcador / reta" },
  { id: "event",       label: "Evento",         icon: CalendarCheck, hint: "Check-in evento" },
];

export default function WalletScan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | scanning | verifying | ok | denied | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [cameras, setCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);
  const stoppingRef = useRef(false);
  const [permMap, setPermMap] = useState({}); // { context: bool }
  const [context, setContext] = useState("club_access");
  const [gate, setGate] = useState("");

  // Gate: only staff can scan (jugadores solo si admin les da permiso vía access-control)
  useEffect(() => {
    if (!user) return;
    api.get("/access/permissions/me")
      .then(({ data }) => {
        const perms = data.permissions || {};
        setPermMap(perms);
        // Si el user actual no puede escanear NINGUN contexto → bloquear
        const hasAny = Object.values(perms).some(Boolean);
        if (!hasAny) {
          toast.error("Tu rol no tiene permiso de escaneo. Pide al admin que active un contexto.");
          navigate("/dashboard/mi-tarjeta", { replace: true });
          return;
        }
        // Elegir el primer contexto permitido si el default (club_access) no lo está
        if (!perms[context]) {
          const firstAllowed = Object.entries(perms).find(([, v]) => v)?.[0];
          if (firstAllowed) setContext(firstAllowed);
        }
      })
      .catch(() => {
        // Fallback legacy: staff genérico
        if (!["admin", "directivo", "profesor"].includes(user.role)) {
          toast.error("Solo staff puede usar el escáner");
          navigate("/dashboard/mi-tarjeta", { replace: true });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Enumerate cameras
  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devs) => {
        setCameras(devs);
        // Prefer the environment/back camera when available
        const back = devs.find((d) => /back|rear|environment/i.test(d.label || ""));
        setActiveCamera((back || devs[0])?.id || null);
      })
      .catch((e) => setError(String(e?.message || e)));
  }, []);

  const stop = async () => {
    const inst = scannerRef.current;
    if (!inst || stoppingRef.current) return;
    stoppingRef.current = true;
    try {
      if (inst.getState && inst.getState() === 2 /* SCANNING */) {
        await inst.stop();
      }
      await inst.clear();
    } catch (_) { /* no-op */ }
    stoppingRef.current = false;
    scannerRef.current = null;
    if (status === "scanning") setStatus("idle");
  };

  const verify = async (token) => {
    setStatus("verifying");
    try {
      const { data } = await api.post("/access/scan", {
        token,
        context,
        gate: gate.trim() || undefined,
      });
      setResult(data);
      setStatus(data.authorized ? "ok" : "denied");
      if (data.authorized) {
        toast.success(`✓ AUTORIZADO — ${data.user?.name || ""}`);
      } else {
        toast.error(`✗ DENEGADO — ${data.reason || "Sin permiso"}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "QR inválido o expirado");
      setStatus("error");
      toast.error(err.response?.data?.detail || "QR inválido o expirado");
    }
  };

  const start = async () => {
    if (!activeCamera) { toast.error("Sin cámaras disponibles"); return; }
    setError("");
    setResult(null);
    setStatus("scanning");
    const inst = new Html5Qrcode(READER_ID, /* verbose */ false);
    scannerRef.current = inst;
    try {
      await inst.start(
        activeCamera,
        { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
        async (decodedText) => {
          // First hit wins — stop the camera and process the payload.
          try { await inst.stop(); } catch (_) {}
          // The QR value can be either the raw JWT (backend) or a URL like
          // /wallet/<jwt>. Peel off the last path segment if it looks like a URL.
          let token = decodedText.trim();
          const slash = token.lastIndexOf("/");
          if (slash !== -1 && token.slice(slash + 1).split(".").length === 3) {
            token = token.slice(slash + 1);
          }
          verify(token);
        },
        () => { /* onDecodeFailure — one per frame, silent */ }
      );
    } catch (e) {
      setError(String(e?.message || e));
      setStatus("error");
    }
  };

  useEffect(() => {
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rescan = async () => {
    await stop();
    setResult(null);
    setError("");
    setStatus("idle");
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="wallet-scan-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold">DYD Wallet · Escáner</p>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tighter">Verificar QR de jugador</h1>
          <p className="text-xs text-gray-500 mt-1">Apunta la cámara al QR de la Wallet Card del jugador. La app confirmará su identidad.</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)} className="border-white/20 text-gray-300 hover:text-[#CCFF00] text-xs uppercase h-9" data-testid="wallet-scan-back">
          <ArrowLeft size={14} className="mr-1" /> Volver
        </Button>
      </div>

      {/* Context selector — determines which permission gate applies */}
      <div className="card-tactical p-4 border-white/10" data-testid="wallet-scan-context">
        <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#CCFF00] mb-2 flex items-center gap-1.5">
          <ShieldCheck size={12} weight="fill" /> Contexto del check-in
        </p>
        <div className="flex flex-wrap gap-2">
          {CONTEXTS.map((c) => {
            const Icon = c.icon;
            const on = context === c.id;
            const allowed = permMap[c.id];
            return (
              <button
                key={c.id}
                onClick={() => allowed && setContext(c.id)}
                disabled={!allowed}
                title={allowed ? c.hint : "Sin permiso para este contexto"}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] uppercase tracking-widest font-bold border transition-colors ${on ? "bg-[#CCFF00] text-black border-[#CCFF00] shadow-[0_0_10px_rgba(204,255,0,0.35)]" : allowed ? "bg-white/5 text-white border-white/15 hover:border-[#CCFF00]/40" : "bg-black/40 text-gray-600 border-white/5 cursor-not-allowed opacity-60"}`}
                data-testid={`ctx-${c.id}`}
              >
                <Icon size={12} weight={on ? "fill" : "regular"} />
                {c.label}
                {!allowed && <LockKey size={10} className="opacity-70" />}
              </button>
            );
          })}
        </div>
        {context === "club_access" && (
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Puerta / Turno (opcional)</label>
            <input
              value={gate}
              onChange={(e) => setGate(e.target.value)}
              placeholder="Ej: Puerta Norte, Recepción, Cancha 3…"
              className="w-full bg-black/60 border border-white/15 text-xs px-3 py-2 text-white focus:border-[#CCFF00] focus:outline-none"
              data-testid="wallet-scan-gate"
            />
          </div>
        )}
      </div>

      {/* Camera view */}
      <div className="card-tactical p-4 relative overflow-hidden border-[#CCFF00]/25" data-testid="wallet-scan-camera">
        <div
          id={READER_ID}
          className="w-full min-h-[320px] bg-black rounded flex items-center justify-center overflow-hidden"
        >
          {status === "idle" && (
            <div className="text-center px-6 py-10" data-testid="wallet-scan-idle">
              <Camera size={44} className="mx-auto text-[#CCFF00] mb-3" />
              <p className="text-sm text-white mb-1">Cámara desactivada</p>
              <p className="text-[11px] text-gray-500">Pulsa "Iniciar escáner" para pedir permisos.</p>
            </div>
          )}
          {status === "verifying" && (
            <div className="text-center py-10">
              <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin mx-auto mb-3" />
              <p className="text-sm text-white">Verificando identidad…</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {status !== "scanning" ? (
            <Button onClick={start} disabled={!activeCamera} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-9" data-testid="wallet-scan-start">
              <Camera size={14} className="mr-1" /> Iniciar escáner
            </Button>
          ) : (
            <Button onClick={stop} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold uppercase tracking-wide text-xs h-9" data-testid="wallet-scan-stop">
              <X size={14} className="mr-1" /> Detener
            </Button>
          )}
          {result && (
            <Button onClick={rescan} variant="outline" className="border-white/20 text-gray-300 hover:text-[#CCFF00] text-xs uppercase h-9" data-testid="wallet-scan-rescan">
              <ArrowsClockwise size={14} className="mr-1" /> Escanear otro
            </Button>
          )}
          {cameras.length > 1 && (
            <select
              value={activeCamera || ""}
              onChange={(e) => setActiveCamera(e.target.value)}
              disabled={status === "scanning"}
              className="ml-auto appearance-none bg-black/60 border border-white/15 text-[11px] uppercase tracking-widest px-3 py-1.5 text-gray-300 focus:border-[#CCFF00] focus:outline-none cursor-pointer disabled:opacity-50"
              data-testid="wallet-scan-camera-select"
            >
              {cameras.map((c) => (
                <option key={c.id} value={c.id}>{c.label || `Cámara ${c.id.slice(0, 6)}`}</option>
              ))}
            </select>
          )}
        </div>

        {error && status === "error" && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300" data-testid="wallet-scan-error">
            {error}
          </div>
        )}
      </div>

      {/* Big AUTHORIZED / DENIED banner */}
      {result && (status === "ok" || status === "denied") && (
        <div
          className={`card-tactical p-6 flex items-center gap-4 ${status === "ok" ? "border-[#CCFF00] bg-[#CCFF00]/5 shadow-[0_0_32px_rgba(204,255,0,0.25)]" : "border-red-500 bg-red-500/10 shadow-[0_0_32px_rgba(239,68,68,0.25)]"}`}
          data-testid={`wallet-scan-decision-${status}`}
        >
          {status === "ok" ? (
            <CheckCircle size={64} weight="fill" className="text-[#CCFF00] shrink-0" />
          ) : (
            <XCircle size={64} weight="fill" className="text-red-500 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className={`font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none ${status === "ok" ? "text-[#CCFF00]" : "text-red-500"}`}>
              {status === "ok" ? "AUTORIZADO" : "DENEGADO"}
            </p>
            <p className="text-xs text-gray-300 mt-1">
              Contexto: <b>{CONTEXTS.find((c) => c.id === result.context)?.label || result.context}</b>
              {result.hardware?.gate && <> · Puerta: <b>{result.hardware.gate}</b></>}
            </p>
            {result.reason && <p className="text-xs text-red-300 mt-1">Motivo: {result.reason}</p>}
            <p className="text-[10px] text-gray-500 mt-1 italic">Integración de hardware: stub · acción sugerida: {result.hardware?.action}</p>
          </div>
        </div>
      )}

      {/* Result card */}
      {result && result.user && (
        <div className="card-tactical p-6 border-[#CCFF00]/30" data-testid="wallet-scan-result">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle size={22} weight="fill" className="text-[#CCFF00]" />
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold">Datos del portador</p>
          </div>
          <div className="flex items-start gap-4 mb-4">
            <UserAvatar
              src={result.user.photo || getAvatarUrl({ name: result.user.name, id: result.user.user_id })}
              fallback={getInitials(result.user.name || "?")}
              className="w-20 h-20 rounded-full border-2 border-[#CCFF00]/40 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase truncate" data-testid="wallet-scan-result-name">{result.user.name || "Sin nombre"}</h2>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <Badge variant="outline" className="text-[10px] uppercase h-5 border-[#CCFF00]/30 text-[#CCFF00]" data-testid="wallet-scan-result-role">{result.user.role}</Badge>
                {result.user.category && <Badge variant="outline" className="text-[10px] uppercase h-5 border-white/15 text-gray-300">Cat {result.user.category}</Badge>}
                {result.user.is_active === false && <Badge variant="outline" className="text-[10px] uppercase h-5 border-red-500/30 text-red-400">Cuenta desactivada</Badge>}
              </div>
              <p className="text-[10px] text-gray-500 mt-1 font-mono">ID · {(result.user.user_id || "").slice(0, 8)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs" data-testid="wallet-scan-result-details">
            {result.user.email && (
              <div className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded">
                <EnvelopeSimple size={14} className="text-gray-500" />
                <span className="text-gray-200 truncate">{result.user.email}</span>
              </div>
            )}
            {result.user.phone && (
              <div className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded">
                <Phone size={14} className="text-gray-500" />
                <span className="text-gray-200">{result.user.phone}</span>
              </div>
            )}
            {result.user.club_id && (
              <div className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded col-span-full">
                <Buildings size={14} className="text-gray-500" />
                <span className="text-gray-200 text-[11px] font-mono">{result.user.club_id}</span>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={() => navigate(`/dashboard/jugador/${result.user.user_id}`)}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-9"
              data-testid="wallet-scan-result-profile"
            >
              <UserIcon size={14} className="mr-1" /> Ver perfil completo
            </Button>
            <Button onClick={rescan} variant="outline" className="border-white/20 text-gray-300 hover:text-[#CCFF00] text-xs uppercase h-9" data-testid="wallet-scan-result-rescan">
              <ArrowsClockwise size={14} className="mr-1" /> Escanear otro
            </Button>
          </div>
        </div>
      )}

      <div className="text-[11px] text-gray-500 leading-relaxed border-t border-white/5 pt-4">
        <ShieldCheck size={14} className="inline text-[#CCFF00] mr-1" />
        El QR se firma en el servidor y caduca cada 24h. Si el jugador presenta un QR expirado, pídele "Renovar QR" en su Wallet Card.
      </div>
    </div>
  );
}
